import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Mail } from "lucide-react";
import { api } from "../lib/api";
import { CONTACT_EMAIL } from "../mock";

const money = (n, c) => `${c} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OfferPage() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { api.get(`/offers/${token}`).then(({ data }) => setOffer(data)).catch(() => setError("This offer link is invalid or has been withdrawn.")); }, [token]);

  if (error) return <main className="min-h-screen bg-[#ece2cf] flex items-center justify-center px-6 pt-24"><p className="text-[#10271a] font-display text-2xl" data-testid="offer-error">{error}</p></main>;
  if (!offer) return <main className="min-h-screen bg-[#ece2cf] pt-32 text-center text-[#5e6b61]">Loading offer...</main>;
  const expired = new Date(offer.valid_until) < new Date(new Date().toDateString());

  return (
    <main className="bg-[#ece2cf] min-h-screen pt-28 md:pt-32 pb-24 print:bg-white print:pt-0 print:pb-0" data-testid="offer-page">
      <div className="mx-auto max-w-[900px] px-4 md:px-10">
        <div className="no-print flex items-center justify-between gap-4 mb-8">
          <p className="text-[#5e6b61] text-sm">Private offer · forward freely within your organisation</p>
          <div className="flex items-center gap-3">
            <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Offer ${offer.reference} — ${offer.buyer.company}`)}`} data-testid="offer-reply" className="inline-flex items-center gap-2 border border-[#1f4a33]/30 text-[#1f4a33] px-5 py-3 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold hover:border-[#1f4a33]"><Mail size={15} /> Reply to trade desk</a>
            <button type="button" onClick={() => window.print()} data-testid="offer-download" className="inline-flex items-center gap-2 bg-[#1f4a33] text-[#f6f1e7] px-5 py-3 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-[#10271a]"><Download size={15} /> Download PDF</button>
          </div>
        </div>

        <article className="spec-sheet bg-[#faf6ec] rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-40px_rgba(16,39,26,0.5)] print:shadow-none print:rounded-none">
          <header className="bg-[#10271a] text-[#f6f1e7] px-8 md:px-12 py-8 flex items-start justify-between gap-6">
            <div>
              <p className="text-[#b08a5f] text-[11px] tracking-[0.25em] uppercase">Indicative offer · {offer.incoterm}</p>
              <h1 className="font-display text-4xl sm:text-5xl font-light leading-[1.0] mt-3">{offer.buyer.company}</h1>
              <p className="mt-3 text-[#f6f1e7]/75">Attn. {offer.buyer.name} · {offer.buyer.country}{offer.buyer.port ? ` · ${offer.buyer.port}` : ""}</p>
            </div>
            <div className="text-right shrink-0">
              <img src="/logo-white.png" alt="Leslie West Africa Trading Company" className="h-12 w-auto ml-auto" />
              <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-[#f6f1e7]/60">Offer reference</p>
              <p className="font-mono text-sm" data-testid="offer-reference">{offer.reference}</p>
            </div>
          </header>

          <div className="px-8 md:px-12 py-8">
            <table className="w-full text-sm" data-testid="offer-lines">
              <thead><tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#7c5230] border-b border-[#10271a]/10">
                <th className="py-3 pr-4">Commodity · grade</th><th className="py-3 pr-4">Volume</th><th className="py-3 pr-4">Price</th><th className="py-3 text-right">Line total</th></tr></thead>
              <tbody className="divide-y divide-[#10271a]/8">
                {offer.lines.map((l, i) => (
                  <tr key={i}>
                    <td className="py-4 pr-4"><p className="font-display text-xl text-[#10271a]">{l.commodity_name}</p><p className="text-[#5e6b61]">{l.grade}{l.frequency ? ` · ${l.frequency}` : ""}</p></td>
                    <td className="py-4 pr-4 text-[#10271a] tabular-nums">{l.volume} {l.unit}</td>
                    <td className="py-4 pr-4 text-[#10271a] tabular-nums">{money(l.price, offer.currency)} / {l.price_unit}</td>
                    <td className="py-4 text-right text-[#10271a] font-medium tabular-nums">{money(l.total, offer.currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t border-[#10271a]/20"><td colSpan={3} className="py-4 text-[11px] tracking-[0.2em] uppercase text-[#7c5230]">Total ({offer.incoterm})</td><td className="py-4 text-right font-display text-2xl text-[#10271a] tabular-nums" data-testid="offer-grand-total">{money(offer.grand_total, offer.currency)}</td></tr></tfoot>
            </table>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
              <Cell label="Valid until" value={offer.valid_until} warn={expired} />
              <Cell label="Payment" value={offer.payment_terms || "On request"} />
              <Cell label="Shipment" value={offer.shipment_window || "To be agreed"} />
              <Cell label="Issued" value={new Date(offer.created_at).toLocaleDateString("en-GB", { dateStyle: "medium" })} />
            </div>
            {offer.notes && <div className="mt-8 rounded-2xl bg-[#ece2cf]/60 p-5 text-sm text-[#10271a] whitespace-pre-wrap" data-testid="offer-notes">{offer.notes}</div>}
            {expired && <p className="mt-6 text-sm text-[#7c5230]" data-testid="offer-expired">This offer has passed its validity date. Contact the trade desk for a refreshed price.</p>}
          </div>

          <footer className="px-8 md:px-12 py-6 bg-[#ece2cf]/60 text-xs text-[#5e6b61] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p>Leslie West Africa Trading Company · <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1f4a33]">{CONTACT_EMAIL}</a></p>
            <p>Indicative, subject to contract and final availability. Ref. enquiry {offer.enquiry_reference}.</p>
          </footer>
        </article>
      </div>
    </main>
  );
}

function Cell({ label, value, warn }) {
  return <div><p className="text-[10px] tracking-[0.18em] uppercase text-[#7c5230] mb-1">{label}</p><p className={`font-medium ${warn ? "text-[#7c5230]" : "text-[#10271a]"}`}>{value}</p></div>;
}
